import { useEffect, useState } from "react";
import { Activity, AlertTriangle, Database, ShieldCheck, Users, Watch } from "lucide-react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";

function Metric({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="font-mono text-xs uppercase tracking-[0.2em]">{label}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-medium" data-testid={`${testId}-value`}>{value ?? "—"}</p>
    </div>
  );
}

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => setError("Unable to load admin metrics."));
    api.get("/admin/health").then(({ data }) => setHealth(data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Admin</h1>
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            Operational metrics only. Private memory content is never displayed here.
          </p>
        </div>

        {error && <p className="text-sm text-destructive" data-testid="admin-error">{error}</p>}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon={Users} label="Registered users" value={overview?.users_total} testId="admin-metric-users-count" />
          <Metric icon={Activity} label="Active accounts" value={overview?.users_active} testId="admin-metric-active-count" />
          <Metric icon={Watch} label="Paired devices" value={overview?.devices_active} testId="admin-metric-devices-count" />
          <Metric icon={Database} label="Total captures" value={overview?.captures_total} testId="admin-metric-captures-count" />
          <Metric icon={Database} label="Captures today" value={overview?.captures_today} testId="admin-metric-captures-today" />
          <Metric icon={AlertTriangle} label="Processing failures" value={overview?.processing_failures} testId="admin-metric-failures-count" />
          <Metric icon={AlertTriangle} label="System errors" value={overview?.system_errors} testId="admin-metric-errors-count" />
          <Metric icon={Activity} label="API health" value={health ? (health.api === "ok" && health.database === "ok" ? "OK" : "Degraded") : null} testId="admin-metric-api-health" />
          <Metric icon={Database} label="Audio captures" value={overview?.audio_captures_total} testId="admin-metric-audio-captures" />
          <Metric icon={AlertTriangle} label="Processing pending" value={overview?.audio_captures_pending} testId="admin-metric-captures-pending" />
          <Metric icon={AlertTriangle} label="Processing failed" value={overview?.audio_captures_failed} testId="admin-metric-captures-failed" />
          <Metric icon={Database} label="Audio storage (MB)" value={overview ? (overview.audio_storage_bytes / (1024 * 1024)).toFixed(2) : null} testId="admin-metric-storage-bytes" />
        </div>

        <section className="rounded-2xl border border-border bg-card p-6" data-testid="admin-events-section">
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Recent system events</h2>
          {health?.recent_events?.length ? (
            <ul className="mt-4 space-y-2">
              {health.recent_events.map((e, i) => (
                <li key={i} className="flex items-center justify-between text-sm text-muted-foreground" data-testid={`admin-event-${i}`}>
                  <span>{e.type} · {e.path}</span>
                  <span className="font-mono text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="admin-no-events">No system events recorded.</p>
          )}
        </section>
      </div>
    </Layout>
  );
}
